import { Box, FormControlLabel, makeStyles, Radio, RadioGroup, Typography } from '@material-ui/core'
import { useField } from 'react-final-form'
import { ChangeEvent } from 'react'

import { DocumentTopic, SubDocumentTopic } from '../../../../services/web/types/requestWebServiceTypes'
import useTranslate from '../../../../i18n/useTranslate'
import Textfield from '../../../../control/textfield/textfield'

export interface DocumentTopicFieldProps {
    name: string
    disabled?: boolean
}

const DocumentTopicField = (props: DocumentTopicFieldProps) => {
    const { name, disabled } = props
    const classes = useStyles()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage

    const { input } = useField<DocumentTopic>(name)

    const selectedId = input.value?.answers?.find((e) => e.checked)?.answerTopicId ?? undefined
    const topicName = currentLanguage === 'th' ? input.value?.nameTh : input.value?.nameEn

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const selectedId = Number(e.target.value)
        const updatedAnswers: DocumentTopic = {
            ...input.value,
            answers: input.value.answers?.map((a) => ({
                ...a,
                checked: a.answerTopicId === selectedId,
            })),
        }
        input.onChange(updatedAnswers)
    }

    return (
        <Box>
            <Typography className={classes.topicHeadline}>{topicName}</Typography>

            {input.value.subTopics && input.value.subTopics.length > 0 ? (
                input.value.subTopics.map((_, index) => (
                    <SubDocumentTopicField no={index + 1} name={`${name}.subTopics[${index}]`} disabled={disabled} />
                ))
            ) : (
                <>
                    <RadioGroup
                        row
                        className={classes.radio}
                        name={`${name}.answers`}
                        value={selectedId ?? ''}
                        onChange={onChange}
                    >
                        {input.value.answers?.map((answer) => (
                            <FormControlLabel
                                key={answer.answerTopicId}
                                value={answer.answerTopicId}
                                control={<Radio color="primary" disabled={disabled} />}
                                label={
                                    <Typography variant="body2">
                                        {currentLanguage === 'th' ? answer.nameTh : answer.nameEn}
                                    </Typography>
                                }
                            />
                        ))}
                    </RadioGroup>
                    <Textfield
                        name={`${name}.comment`}
                        materialInputProps={{
                            label: 'หมายเหตุ',
                            placeholder: 'กรอกหมายเหตุ',
                            size: 'small',
                            rows: 2,
                            multiline: true,
                            disabled,
                        }}
                        maxLength={1000}
                    />
                </>
            )}
        </Box>
    )
}

export interface SubDocumentTopicFieldProps {
    no: number
    name: string
    disabled?: boolean
}

const SubDocumentTopicField = (props: SubDocumentTopicFieldProps) => {
    const { no, name, disabled } = props
    const classes = useStyles()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage

    const { input } = useField<SubDocumentTopic>(name)

    const selectedId = input.value?.answers?.find((e) => e.checked)?.answerTopicId ?? undefined
    const subTopicName = currentLanguage === 'th' ? input.value?.nameTh : input.value?.nameEn

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const selectedId = Number(e.target.value)
        const updatedAnswers: SubDocumentTopic = {
            ...input.value,
            answers: input.value.answers?.map((a) => ({
                ...a,
                checked: a.answerTopicId === selectedId,
            })),
        }
        input.onChange(updatedAnswers)
    }

    return (
        <Box>
            <Typography className={classes.subTopicHeadline}>
                {no}. {subTopicName}
            </Typography>

            <RadioGroup
                row
                className={classes.radio}
                name={`${name}.answers`}
                value={selectedId ?? ''}
                onChange={onChange}
            >
                {input.value.answers?.map((answer) => (
                    <FormControlLabel
                        key={answer.answerTopicId}
                        value={answer.answerTopicId}
                        control={<Radio color="primary" disabled={disabled} />}
                        label={
                            <Typography variant="body2">
                                {currentLanguage === 'th' ? answer.nameTh : answer.nameEn}
                            </Typography>
                        }
                    />
                ))}
            </RadioGroup>

            <Textfield
                name={`${name}.comment`}
                materialInputProps={{
                    label: 'หมายเหตุ',
                    placeholder: 'กรอกหมายเหตุ',
                    size: 'small',
                    rows: 2,
                    multiline: true,
                    disabled,
                }}
                maxLength={1000}
            />
        </Box>
    )
}

const useStyles = makeStyles(() => ({
    topicHeadline: {
        fontSize: 16,
        fontWeight: 700,
        marginBottom: 16,
    },
    subTopicHeadline: {
        fontSize: 16,
        fontWeight: 400,
        marginBottom: 8,
    },
    radio: {
        marginBottom: 8,
    },
}))

export default DocumentTopicField
